import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import { toast as sonnerToast } from 'sonner';
import { BACKEND_URL } from '../config/canonical';
import { Award, Save, Eye, RefreshCw, Undo2, FilePlus, Trash2 } from 'lucide-react';

/**
 * Master Directive Phase 14 — Nexus Training Center Certificate Editor.
 *
 * Admins select a certification, edit the overlay text fields, and preview
 * the branded PDF live. Overrides never resize or move the underlying
 * template — the render engine auto-fits long strings.
 */
const FIELDS = [
  { key: 'custom_title', label: 'Title (overrides baked NEXUS CERTIFIED INVESTIGATOR I)', placeholder: 'e.g. NEXUS CERTIFIED SURVEILLANCE OPERATOR II' },
  { key: 'custom_subtitle', label: 'Subtitle', placeholder: 'e.g. Class of 2026 · Honors Track' },
  { key: 'custom_recipient_prefix', label: 'Recipient prefix', placeholder: 'e.g. This certificate is presented to' },
  { key: 'custom_body_line', label: 'Body line', placeholder: 'e.g. In recognition of distinguished field service and…' },
  { key: 'custom_signatory_left', label: 'Signatory (left)', placeholder: 'Chief Instructor · D. Roberts' },
  { key: 'custom_signatory_right', label: 'Signatory (right)', placeholder: 'Director · X. Smith' },
  { key: 'custom_footer', label: 'Footer', placeholder: 'Verified at nexustrac.org/training-center/certificate/…' },
];

const EMPTY = FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: '' }), {});

const AdminCertificateEditor = () => {
  const token = localStorage.getItem('nexus_token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [catalog, setCatalog] = useState([]);
  const [certId, setCertId] = useState('');
  const [values, setValues] = useState(EMPTY);
  const [initial, setInitial] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  // Iter141 — new blank template dialog state
  const [newOpen, setNewOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newSummary, setNewSummary] = useState('');
  const [newPassing, setNewPassing] = useState(80);
  const [newValidity, setNewValidity] = useState(24);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/training-center/certifications`, { headers });
      if (r.ok) {
        const d = await r.json();
        setCatalog(d.catalog || []);
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally { setLoading(false); }
  }, [headers]);

  const loadCertText = useCallback(async (id) => {
    if (!id) return;
    try {
      const r = await fetch(`${BACKEND_URL}/api/training-center/admin/certifications/${id}/text`, { headers });
      if (r.ok) {
        const d = await r.json();
        const next = { ...EMPTY, ...(d.overrides || {}) };
        setValues(next);
        setInitial(next);
      } else if (r.status === 403) {
        toast({ title: 'Admin access required', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  }, [headers]);

  useEffect(() => { loadCatalog(); }, [loadCatalog]);
  useEffect(() => { if (certId) loadCertText(certId); }, [certId, loadCertText]);

  const dirty = FIELDS.some((f) => (values[f.key] || '') !== (initial[f.key] || ''));

  const save = async () => {
    if (!certId) return;
    setSaving(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/training-center/admin/certifications/${certId}/text`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ overrides: values }),
      });
      if (r.ok) {
        const d = await r.json();
        setInitial({ ...EMPTY, ...(d.overrides || {}) });
        toast({ title: 'Certificate text saved' });
      } else {
        const d = await r.json().catch(() => ({}));
        toast({ title: 'Save failed', description: d?.detail || 'Unable to save', variant: 'destructive' });
      }
    } finally { setSaving(false); }
  };

  const preview = async () => {
    if (!certId) return;
    // Save first so preview reflects the latest edits
    if (dirty) await save();
    // Fetch preview as blob with auth header, then render inside iframe
    try {
      const r = await fetch(`${BACKEND_URL}/api/training-center/admin/certificate-text/preview/${certId}`, { headers });
      if (!r.ok) {
        toast({ title: 'Preview failed', variant: 'destructive' });
        return;
      }
      const blob = await r.blob();
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const revert = () => setValues(initial);

  // Iter141 — create a brand-new blank certification template
  const createBlank = async () => {
    if (newTitle.trim().length < 3) {
      sonnerToast.error('Title too short', { description: 'Please enter at least 3 characters.' });
      return;
    }
    setCreating(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/training-center/admin/certifications`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: newTitle.trim(),
          summary: newSummary.trim(),
          passing_score: Number(newPassing) || 80,
          validity_months: Number(newValidity) || 24,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        sonnerToast.error('Could not create template', { description: d?.detail || 'Unknown error' });
        return;
      }
      sonnerToast.success('Blank certificate template created', {
        description: `${d.id} is ready. You can now edit its overlay text.`,
      });
      setNewOpen(false);
      setNewTitle(''); setNewSummary(''); setNewPassing(80); setNewValidity(24);
      await loadCatalog();
      setCertId(d.id);
    } finally { setCreating(false); }
  };

  const deleteTemplate = async () => {
    if (!activeCert?.custom_template) return;
    if (!window.confirm(`Delete template "${activeCert.cert_title || activeCert.title}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/training-center/admin/certifications/${certId}`, {
        method: 'DELETE', headers,
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        sonnerToast.error('Delete failed', { description: d?.detail || 'Unknown error' });
        return;
      }
      sonnerToast.success('Template deleted');
      setCertId('');
      await loadCatalog();
    } finally { setDeleting(false); }
  };

  const activeCert = catalog.find((c) => c.id === certId);

  return (
    <div className="space-y-6" data-testid="admin-cert-editor">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 flex items-center justify-center rounded-lg" style={{ background: '#B45309', boxShadow: '0 6px 14px -4px rgba(217,119,6,0.55)' }}>
            <Award className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="nx-eyebrow">Admin · Training Center</div>
            <h1 className="nx-h1 mt-1">Certificate Text Editor</h1>
            <p className="nx-body mt-1">Retitle and rewrite any Nexus Training Center certificate without breaking the layout.</p>
          </div>
        </div>
        <Button variant="outline" onClick={loadCatalog} className="border-[#334155] text-[#94A3B8] hover:bg-[#182136]" data-testid="cert-editor-refresh">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
        <Button onClick={() => setNewOpen(true)}
                className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
                data-testid="cert-editor-new-blank">
          <FilePlus className="w-4 h-4 mr-2" /> New Blank Template
        </Button>
      </div>

      {loading ? (
        <div className="p-10 text-center text-slate-400" data-testid="cert-editor-loading">
          <RefreshCw className="w-6 h-6 mx-auto mb-2 animate-spin" />
          Loading certifications…
        </div>
      ) : catalog.length === 0 ? (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-6 text-center" data-testid="cert-editor-empty">
          <Award className="w-10 h-10 text-amber-300 mx-auto mb-2" />
          <h3 className="text-amber-100 font-semibold mb-1">No certifications available</h3>
          <p className="text-sm text-amber-200/80">
            Either the Training Center catalog has not been seeded yet, or your
            account does not have the <code>admin_edit_certificates</code>
            permission. Ask Main Hub to grant Training Center admin access and
            then click Retry.
          </p>
          <Button onClick={loadCatalog} className="mt-3 bg-amber-500 hover:bg-amber-400 text-slate-900" data-testid="cert-editor-retry">
            <RefreshCw className="w-4 h-4 mr-1" /> Retry
          </Button>
        </div>
      ) : (
      <Card className="bg-[#131A2A] border-[#334155]">
        <CardHeader className="pb-3"><CardTitle className="text-[#F8FAFC] text-base">Certification</CardTitle></CardHeader>
        <CardContent>
          <Select value={certId} onValueChange={setCertId}>
            <SelectTrigger data-testid="cert-select" className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC] max-w-md">
              <SelectValue placeholder="Choose a certification to edit" />
            </SelectTrigger>
            <SelectContent className="bg-[#131A2A] border-[#334155]">
              {catalog.map((c) => (
                <SelectItem key={c.id} value={c.id} className="text-[#F8FAFC]">
                  {c.cert_title || c.name || c.id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {activeCert && (
            <div className="flex flex-wrap items-center justify-between gap-2 mt-2">
              <p className="text-xs text-[#94A3B8]">
                Editing: <span className="text-[#F8FAFC] font-mono">{activeCert.id}</span>
                {activeCert.course_ids?.length ? ` · ${activeCert.course_ids.length} courses` : ''}
                {activeCert.custom_template ? (
                  <span className="ml-2 text-[10px] uppercase tracking-wider text-amber-300 border border-amber-500/40 rounded px-1.5 py-0.5">
                    Admin-created
                  </span>
                ) : (
                  <span className="ml-2 text-[10px] uppercase tracking-wider text-sky-300 border border-sky-500/40 rounded px-1.5 py-0.5">
                    Canonical
                  </span>
                )}
              </p>
              {activeCert.custom_template && (
                <Button onClick={deleteTemplate} disabled={deleting}
                        variant="outline"
                        className="border-red-500/40 text-red-300 hover:bg-red-500/10"
                        data-testid="cert-editor-delete-template">
                  <Trash2 className="w-4 h-4 mr-2" /> {deleting ? 'Deleting…' : 'Delete template'}
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {certId && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="bg-[#131A2A] border-[#334155]">
            <CardHeader className="pb-3">
              <CardTitle className="text-[#F8FAFC] text-base">Overlay fields</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {FIELDS.map((f) => (
                <div key={f.key} className="space-y-1.5">
                  <Label className="text-xs text-[#94A3B8] uppercase tracking-wider">{f.label}</Label>
                  {f.key === 'custom_body_line' ? (
                    <Textarea
                      value={values[f.key]}
                      onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                      placeholder={f.placeholder}
                      rows={3}
                      className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]"
                      data-testid={`cert-field-${f.key}`}
                    />
                  ) : (
                    <Input
                      value={values[f.key]}
                      onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                      placeholder={f.placeholder}
                      className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]"
                      data-testid={`cert-field-${f.key}`}
                    />
                  )}
                </div>
              ))}
              <div className="flex flex-wrap gap-2 pt-2">
                <Button onClick={save} disabled={!dirty || saving} data-testid="save-btn"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60">
                  <Save className="w-4 h-4 mr-2" /> Save
                </Button>
                <Button onClick={preview} data-testid="preview-btn"
                        className="bg-[#1D4ED8] hover:bg-[#1E40AF] text-white">
                  <Eye className="w-4 h-4 mr-2" /> Save &amp; Preview
                </Button>
                <Button onClick={revert} disabled={!dirty} variant="outline"
                        className="border-[#334155] text-[#94A3B8] hover:bg-[#182136]">
                  <Undo2 className="w-4 h-4 mr-2" /> Revert
                </Button>
              </div>
              <p className="text-[11px] text-[#94A3B8]">
                Leave a field empty to keep the baked default. Long strings auto-shrink so
                the certificate layout stays intact.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-[#131A2A] border-[#334155]">
            <CardHeader className="pb-3"><CardTitle className="text-[#F8FAFC] text-base">Live preview</CardTitle></CardHeader>
            <CardContent>
              {previewUrl ? (
                <iframe title="Certificate preview" src={previewUrl}
                        className="w-full h-[520px] rounded bg-white border border-[#334155]"
                        data-testid="cert-preview-iframe" />
              ) : (
                <div className="w-full h-[520px] rounded border border-dashed border-[#334155] flex items-center justify-center text-[#64748B] text-sm">
                  Click <span className="mx-1 text-[#F8FAFC]">Save &amp; Preview</span> to render this certificate with your edits.
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Iter141 — New blank certification template dialog */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="bg-[#0F172A] border-[#334155] text-[#F8FAFC] max-w-md" data-testid="cert-new-blank-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FilePlus className="w-5 h-5 text-amber-400" /> New Blank Certificate Template
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs text-[#94A3B8] uppercase tracking-wider">Title *</Label>
              <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)}
                     placeholder="e.g. Advanced Fraud Investigator"
                     className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]"
                     data-testid="cert-new-title" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-[#94A3B8] uppercase tracking-wider">Summary</Label>
              <Textarea value={newSummary} onChange={(e) => setNewSummary(e.target.value)}
                        placeholder="One-sentence description that appears on the Pending Certificates grid."
                        rows={3}
                        className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]"
                        data-testid="cert-new-summary" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-[#94A3B8] uppercase tracking-wider">Passing %</Label>
                <Input type="number" min={0} max={100} value={newPassing}
                       onChange={(e) => setNewPassing(e.target.value)}
                       className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]"
                       data-testid="cert-new-passing" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[#94A3B8] uppercase tracking-wider">Validity (months)</Label>
                <Input type="number" min={0} max={240} value={newValidity}
                       onChange={(e) => setNewValidity(e.target.value)}
                       className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]"
                       data-testid="cert-new-validity" />
              </div>
            </div>
            <p className="text-[11px] text-[#94A3B8]">
              A blank template starts with no required courses. You can assign courses and tweak overlay text after creation.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)}
                    className="border-[#334155] text-[#94A3B8] hover:bg-[#182136]"
                    data-testid="cert-new-cancel">
              Cancel
            </Button>
            <Button onClick={createBlank} disabled={creating || newTitle.trim().length < 3}
                    className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold disabled:opacity-60"
                    data-testid="cert-new-create">
              <FilePlus className="w-4 h-4 mr-2" /> {creating ? 'Creating…' : 'Create Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCertificateEditor;
